/**
 * Simple in-memory HTTP GET cache with TTL. Reduces redundant fetches
 * for endpoints that change rarely (store-settings, shipping-settings,
 * delivery-cities). Cache lives in-tab and is invalidated on reload.
 *
 * Uso:
 *   import { cachedGet, invalidateCache } from './utils/cachedGet';
 *   const data = await cachedGet(`${API}/store-settings`, { ttl: 30_000 });
 *   // Después de un PUT/POST que cambia settings:
 *   invalidateCache(`${API}/store-settings`);
 */
import axios from 'axios';

const cache = new Map(); // url -> { data, expiresAt }
const inflight = new Map(); // url -> Promise (deduplica requests concurrentes)

export const cachedGet = async (url, opts = {}) => {
  const { ttl = 60_000, config } = opts;
  const now = Date.now();

  const entry = cache.get(url);
  if (entry && entry.expiresAt > now) return entry.data;

  const flying = inflight.get(url);
  if (flying) return flying;

  const p = axios.get(url, config).then((r) => {
    cache.set(url, { data: r.data, expiresAt: now + ttl });
    inflight.delete(url);
    return r.data;
  }).catch((err) => {
    inflight.delete(url);
    throw err;
  });

  inflight.set(url, p);
  return p;
};

export const invalidateCache = (url) => {
  if (url) cache.delete(url);
  else cache.clear();
};

export const invalidateCachePrefix = (prefix) => {
  for (const k of cache.keys()) if (k.startsWith(prefix)) cache.delete(k);
};
